import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { number: "01", title: "", description: "", order: 0 };

export default function ServicesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const startEdit = (s) => { setEditingId(s.id); setForm({ number: s.number, title: s.title, description: s.description, order: s.order }); };
  const cancel = () => { setEditingId(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) { toast.error("Title and description are required."); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/services/${editingId}`, form);
      else await api.post("/services", form);
      toast.success(editingId ? "Service updated." : "Service added.");
      cancel(); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/services/${id}`); toast.success("Deleted."); if (editingId === id) cancel(); await load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="services-admin">
      <section className="lg:col-span-7 space-y-3">
        <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Services ({items.length})</p>
        {items.map((s) => (
          <div key={s.id} className={`bg-white border p-4 flex gap-4 items-start ${editingId === s.id ? "border-[#FF3E00]" : "border-neutral-200"}`} data-testid={`service-row-${s.id}`}>
            <div className="font-body text-xs uppercase tracking-[0.2em] text-neutral-500 w-10 flex-shrink-0">{s.number}</div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-base">{s.title}</div>
              <div className="font-body text-sm text-neutral-600 mt-1">{s.description}</div>
              <div className="font-body text-xs text-neutral-400 mt-1">order {s.order}</div>
            </div>
            <button onClick={() => startEdit(s)} className="font-body text-xs uppercase tracking-[0.2em] link-line" data-testid={`svc-edit-${s.id}`}>Edit</button>
            <button onClick={() => remove(s.id)} className="font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line" data-testid={`svc-delete-${s.id}`}>Delete</button>
          </div>
        ))}
        {items.length === 0 && <p className="font-body text-sm text-neutral-500">No services yet.</p>}
      </section>

      <aside className="lg:col-span-5">
        <div className="flex items-center justify-between mb-6">
          <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">{editingId ? "Edit Service" : "New Service"}</p>
          {editingId && <button onClick={cancel} className="font-body text-xs uppercase tracking-[0.2em] link-line">Cancel</button>}
        </div>
        <form onSubmit={submit} className="space-y-1 bg-white border border-neutral-200 p-6">
          <input value={form.number} onChange={onChange("number")} placeholder="Number (e.g. 01)" className="input-bare" data-testid="svc-number-input" />
          <input value={form.title} onChange={onChange("title")} placeholder="Title (e.g. Brand Identity)" className="input-bare" data-testid="svc-title-input" />
          <textarea value={form.description} onChange={onChange("description")} placeholder="Description" rows={3} className="input-bare resize-none" data-testid="svc-description-input" />
          <input type="number" value={form.order} onChange={onChange("order")} placeholder="Order" className="input-bare" data-testid="svc-order-input" />
          <button type="submit" disabled={saving} className="mt-6 w-full bg-[#0A0A0A] text-[#F4F4F0] py-4 font-display font-bold uppercase tracking-widest text-sm hover:bg-[#FF3E00] transition-colors disabled:opacity-40" data-testid="svc-save-button">
            {saving ? "Saving…" : editingId ? "Update Service" : "Add Service"}
          </button>
        </form>
      </aside>
    </div>
  );
}
